import React, { useState, useEffect } from 'react';
import { Table, Button, Modal, Form, Input, message, Space, Card, Typography, Select } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, EyeOutlined } from '@ant-design/icons';
import api from '../../utils/axios';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';

const { Title } = Typography;
const { Option } = Select;

interface EmailTemplate {
  id: string;
  name: string;
  subject: string;
  body: string;
  type: string;
  variables: string[];
  createdAt: string;
  updatedAt: string;
}

const EmailTemplates: React.FC = () => {
  const [templates, setTemplates] = useState<EmailTemplate[]>([]);
  const [loading, setLoading] = useState(false);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isPreviewVisible, setIsPreviewVisible] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState<EmailTemplate | null>(null);
  const [previewTemplate, setPreviewTemplate] = useState<EmailTemplate | null>(null);
  const [form] = Form.useForm();

  const fetchTemplates = async () => {
    setLoading(true);
    try {
      const response = await api.get<{ success: boolean; data: EmailTemplate[] }>('/settings/email-templates');
      if (response.data.success) {
        setTemplates(response.data.data);
      } else {
        message.error('Failed to fetch email templates');
      }
    } catch (error) {
      console.error('Error fetching email templates:', error);
      message.error('Failed to fetch email templates');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTemplates();
  }, []);

  const handleCreate = () => {
    setEditingTemplate(null);
    setIsModalVisible(true);
    form.resetFields();
  };

  const handleEdit = (template: EmailTemplate) => {
    setEditingTemplate(template);
    setIsModalVisible(true);
    form.setFieldsValue(template);
  };

  const handlePreview = (template: EmailTemplate) => {
    setPreviewTemplate(template);
    setIsPreviewVisible(true);
  };

  const handleDelete = async (id: string) => {
    try {
      await api.delete(`/settings/email-templates/${id}`);
      message.success('Template deleted successfully');
      fetchTemplates();
    } catch (error) {
      console.error('Error deleting template:', error);
      message.error('Failed to delete template');
    }
  };

  const handleModalOk = async () => {
    try {
      const values = await form.validateFields();
      if (editingTemplate) {
        await api.put(`/settings/email-templates/${editingTemplate.id}`, values);
        message.success('Template updated successfully');
      } else {
        await api.post('/settings/email-templates', values);
        message.success('Template created successfully');
      }
      setIsModalVisible(false);
      fetchTemplates();
    } catch (error) {
      console.error('Error saving template:', error);
      message.error('Failed to save template');
    }
  };

  const columns = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      sorter: true,
    },
    {
      title: 'Type',
      dataIndex: 'type',
      key: 'type',
      filters: [
        { text: 'Welcome', value: 'welcome' },
        { text: 'Notification', value: 'notification' },
        { text: 'Reminder', value: 'reminder' },
        { text: 'Custom', value: 'custom' },
      ],
    },
    {
      title: 'Subject',
      dataIndex: 'subject',
      key: 'subject',
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_: any, record: EmailTemplate) => (
        <Space>
          <Button
            icon={<EyeOutlined />}
            onClick={() => handlePreview(record)}
            size="small"
          />
          <Button
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
            size="small"
          />
          <Button
            icon={<DeleteOutlined />}
            onClick={() => handleDelete(record.id)}
            size="small"
            danger
          />
        </Space>
      ),
    },
  ];

  return (
    <div className="p-6">
      <Card>
        <div className="flex justify-between items-center mb-4">
          <Title level={4}>Email Templates</Title>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={handleCreate}
          >
            Add Template
          </Button>
        </div>

        <Table
          columns={columns}
          dataSource={templates}
          rowKey="id"
          loading={loading}
        />
      </Card>

      <Modal
        title={editingTemplate ? 'Edit Template' : 'New Template'}
        open={isModalVisible}
        onOk={handleModalOk}
        onCancel={() => setIsModalVisible(false)}
        width={800}
        destroyOnClose
      >
        <Form
          form={form}
          layout="vertical"
        >
          <Form.Item
            name="name"
            label="Template Name"
            rules={[{ required: true, message: 'Please enter template name' }]}
          >
            <Input />
          </Form.Item>

          <Form.Item
            name="type"
            label="Template Type"
            rules={[{ required: true, message: 'Please select template type' }]}
          >
            <Select>
              <Option value="welcome">Welcome</Option>
              <Option value="notification">Notification</Option>
              <Option value="reminder">Reminder</Option>
              <Option value="custom">Custom</Option>
            </Select>
          </Form.Item>

          <Form.Item
            name="subject"
            label="Email Subject"
            rules={[{ required: true, message: 'Please enter email subject' }]}
          >
            <Input />
          </Form.Item>

          <Form.Item
            name="body"
            label="Email Body"
            rules={[{ required: true, message: 'Please enter email body' }]}
          >
            <ReactQuill
              theme="snow"
              style={{ height: '200px', marginBottom: '50px' }}
            />
          </Form.Item>

          <Form.Item
            name="variables"
            label="Available Variables"
            tooltip="These variables can be used in the template using {{variable_name}}"
          >
            <Select mode="tags" placeholder="Add variables">
              <Option value="user_name">User Name</Option>
              <Option value="company_name">Company Name</Option>
              <Option value="date">Date</Option>
              <Option value="time">Time</Option>
            </Select>
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title="Template Preview"
        open={isPreviewVisible}
        onCancel={() => setIsPreviewVisible(false)}
        footer={null}
        width={800}
      >
        {previewTemplate && (
          <div>
            <h3 className="text-lg font-semibold mb-2">{previewTemplate.subject}</h3>
            <div
              className="prose max-w-none"
              dangerouslySetInnerHTML={{ __html: previewTemplate.body }}
            />
          </div>
        )}
      </Modal>
    </div>
  );
};

export default EmailTemplates; 